import React, { useState, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { AGENTS, AgentDef, Feature } from "./agentConfig";
import backArrow from "../../assets/task/back_arrow.svg";
import videoCreateicon from "../../assets/task/video-create-icon.svg";
import contentCreateicon from "../../assets/task/content-create-icon.svg";
import audioIocnimg from "../../assets/task/audio-g-icon.svg";
import imageIocnimg from "../../assets/task/images-create-icon.svg";
import UploadButtonimg from "../../assets/task/UploadButtonimg.svg";
import BreadCumb from "../../components/common/Breadcrumb";
import { useAuth } from "../../context/AuthContext";

/* ── Inline SVG icons ─────────────────────────── */
const ImageFeatureIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
    <rect
      x="3"
      y="3"
      width="18"
      height="18"
      rx="2"
      stroke="white"
      strokeWidth="2"
    />
    <circle cx="8.5" cy="8.5" r="1.5" fill="white" />
    <path
      d="M21 15l-5-5L5 21"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AudioFeatureIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M19 10v2a7 7 0 0 1-14 0v-2"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <line
      x1="12"
      y1="19"
      x2="12"
      y2="23"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <line
      x1="8"
      y1="23"
      x2="16"
      y2="23"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const ScreenFeatureIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="4" width="18" height="12" rx="2" stroke="white" strokeWidth="2" />
    <line x1="8" y1="20" x2="16" y2="20" stroke="white" strokeWidth="2" strokeLinecap="round" />
    <line x1="12" y1="16" x2="12" y2="20" stroke="white" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const LockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <rect
      x="3"
      y="11"
      width="18"
      height="11"
      rx="2"
      stroke="#526484"
      strokeWidth="2"
    />
    <path
      d="M7 11V7a5 5 0 0 1 10 0v4"
      stroke="#526484"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path
      d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"
      stroke="#2b2b2b"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <polyline
      points="7 10 12 15 17 10"
      stroke="#2b2b2b"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <line
      x1="12"
      y1="15"
      x2="12"
      y2="3"
      stroke="#2b2b2b"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const SendIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <line
      x1="22"
      y1="2"
      x2="11"
      y2="13"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <polygon
      points="22 2 15 22 11 13 2 9 22 2"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="white"
    />
  </svg>
);

const SpinnerIcon = () => (
  <svg
    className="animate-spin"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
  >
    <circle
      cx="12"
      cy="12"
      r="10"
      stroke="white"
      strokeWidth="3"
      strokeOpacity="0.3"
    />
    <path
      d="M12 2a10 10 0 0 1 10 10"
      stroke="white"
      strokeWidth="3"
      strokeLinecap="round"
    />
  </svg>
);

/* ── Component ────────────────────────────────── */
// Turns a FastAPI/Pydantic 422 body ({"detail": [{"loc": [...], "msg": "..."}]})
// into a readable "field: message" string; falls back to whatever's in
// `detail`/`error`, then a generic message if the body isn't JSON at all.
const parseApiErrorMessage = async (response: Response): Promise<string> => {
  try {
    const data = await response.json();
    if (Array.isArray(data?.detail)) {
      return data.detail
        .map((d: any) => {
          const field = Array.isArray(d.loc) ? d.loc[d.loc.length - 1] : null;
          return field ? `${field}: ${d.msg}` : d.msg;
        })
        .join("\n");
    }
    if (typeof data?.detail === "string") return data.detail;
    if (typeof data?.error === "string") return data.error;
  } catch {
    // Response body wasn't JSON — fall through to the generic message.
  }
  return `Request failed with status ${response.status}`;
};

const AgentDetail: React.FC = () => {
  const navigate = useNavigate();
  const { agentId } = useParams<{ agentId: string }>();
  const location = useLocation();

  const agent: AgentDef | undefined =
    (location.state as any)?.agent ?? AGENTS.find((a) => a.id === agentId);

  const [selectedFeature, setSelectedFeature] = useState<any>(null);
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [generatedDraft, setGeneratedDraft] = useState<string | null>(null);
  const [redPenExplanation, setRedPenExplanation] = useState<string | null>(null);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  // Current image ko next prompt ke reference ke liye store karega
  const [referenceImageUrl, setReferenceImageUrl] = useState<string | null>(null);
  // Fresh uploaded image. This takes priority over the generated-image chain.
  const [uploadedImagesB64, setUploadedImagesB64] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [generatedAudioUrl, setGeneratedAudioUrl] = useState<string | null>(
    null,
  );

  const [error, setError] = useState<string | null>(null);
  const { userInfo } = useAuth()
  const audioRef = useRef<HTMLAudioElement>(null);
  const employeeID = userInfo?.employee_id

  // ── ScreenForge form state ──────────────────────
  // Derived purely from the uploaded screenshot's filename — stays empty
  // until a file is chosen (no fake placeholder shown in the UI). Backend
  // requires screen_name non-empty, so handleScreenForgeSubmit falls back to
  // a generated value only in the outgoing payload, never in this state.
  const generateFallbackScreenName = () => `screen-${Date.now()}`;
  const [sfScreenName, setSfScreenName] = useState("");
  const [sfOldHtml, setSfOldHtml] = useState("");
  const [sfOldCss, setSfOldCss] = useState("");
  const [sfNotes, setSfNotes] = useState("");
  const [sfScreenshotB64, setSfScreenshotB64] = useState<string | null>(null);
  const [sfScreenshotMime, setSfScreenshotMime] = useState<string>("image/png");
  const [sfScreenshotPreview, setSfScreenshotPreview] = useState<string | null>(null);
  // Stable across retries of the same screen (backend de-dupe key) — one
  // generated per visit to this form, not regenerated on every submit.
  const sfRetryKeyRef = useRef<string>(
    `sf-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  );
  const [sfResult, setSfResult] = useState<any>(null);
  // Final polled record (matched[0] once status !== "processing") — separate
  // from sfResult (which mirrors whatever the last raw API response was, for
  // the JSON debug view) so "View Assets" has one clear, typed source.
  const [sfConversionResult, setSfConversionResult] = useState<any>(null);
  const [sfPolling, setSfPolling] = useState(false);
  const sfScreenshotInputRef = useRef<HTMLInputElement>(null);

  // ── SignalScout feed (days-filterable, read-only) ─
  const SIGNALSCOUT_FEED_BASE_URL =
    "https://testwizkind.caryfy.ai/wizkind_harness/signalscout/feed_by_date";
  const SIGNALSCOUT_DAY_OPTIONS = [1, 3, 7, 14, 30];
  const [ssDays, setSsDays] = useState(7);
  const [ssData, setSsData] = useState<any>(null);
  const [ssLoading, setSsLoading] = useState(false);
  const [ssError, setSsError] = useState<string | null>(null);

  // ── RegSentinel scan (read-only, docs/notices watch) ─
  const REGSENTINEL_SCAN_URL =
    "https://testwizkind.caryfy.ai/wizkind_harness/regsentinel/scan";
  const REGSENTINEL_REFRESH_URL =
    "https://testwizkind.caryfy.ai/wizkind_harness/regsentinel/refresh_sources";
  const REGSENTINEL_DAYS = 35;
  const [rsData, setRsData] = useState<any>(null);
  const [rsLoading, setRsLoading] = useState(false);
  const [rsError, setRsError] = useState<string | null>(null);
  const [rsRefreshing, setRsRefreshing] = useState(false);
  const [rsRefreshMessage, setRsRefreshMessage] = useState<string | null>(null);

  // ── ScreenForge "View Assets" list/detail modal ─
  const [sfAssetsModalOpen, setSfAssetsModalOpen] = useState(false);
  const [sfConversionsList, setSfConversionsList] = useState<any[]>([]);
  const [sfConversionsLoading, setSfConversionsLoading] = useState(false);
  const [sfConversionsError, setSfConversionsError] = useState<string | null>(null);
  const [sfSelectedConversion, setSfSelectedConversion] = useState<any | null>(null);
  const [sfConversionDetailLoading, setSfConversionDetailLoading] = useState(false);

  if (!agent) {
    return (
      <section className="w-full min-h-screen flex flex-col items-center justify-center bg-white">
        <p className="text-base text-headings mb-4">Agent not found.</p>

        <button
          onClick={() => navigate("/agents")}
          className="text-sm font-medium"
          style={{ color: "#019fd5" }}
        >
          ← Back to AI Team
        </button>
      </section>
    );
  }

  const resetScreenForgeForm = () => {
    setSfScreenName("");
    setSfOldHtml("");
    setSfOldCss("");
    setSfNotes("");
    setSfScreenshotB64(null);
    setSfScreenshotPreview(null);
    setSfResult(null);
    setSfConversionResult(null);
    setSfPolling(false);
    sfRetryKeyRef.current = `sf-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  };

  const fetchSignalScoutFeed = async (days: number = ssDays) => {
    setSsLoading(true);
    setSsError(null);

    try {
      const response = await fetch(`${SIGNALSCOUT_FEED_BASE_URL}?days=${days}`);

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const data = await response.json();
      setSsData(data);
    } catch (err: any) {
      setSsError(err?.message || "Failed to load SignalScout feed.");
    } finally {
      setSsLoading(false);
    }
  };

  const fetchRegSentinelScan = async () => {
    setRsLoading(true);
    setRsError(null);

    try {
      const response = await fetch(REGSENTINEL_SCAN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          days: REGSENTINEL_DAYS,
          all_documents: false,
          max_items: 12,
        }),
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const data = await response.json();
      setRsData(data);
    } catch (err: any) {
      setRsError(err?.message || "Failed to load RegSentinel scan.");
    } finally {
      setRsLoading(false);
    }
  };

  const handleRegSentinelRefresh = async () => {
    setRsRefreshing(true);
    setRsRefreshMessage(null);
    setRsError(null);

    try {
      const response = await fetch(REGSENTINEL_REFRESH_URL, { method: "POST" });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      setRsRefreshMessage(
        "Refresh queued — fetching sources in the background, this can take 2–15 minutes. Results below are from the last completed scan; run Refresh sources again later, then reopen this card, to see updates."
      );
    } catch (err: any) {
      setRsError(err?.message || "Failed to queue RegSentinel refresh.");
    } finally {
      setRsRefreshing(false);
    }
  };

  const handleFeatureSelect = (feature: Feature) => {
    setSelectedFeature(feature);
    setPrompt("");
    setGeneratedImage(null);
    setReferenceImageUrl(null);
    setUploadedImagesB64([]);
    setGeneratedAudioUrl(null);
    setGeneratedDraft(null);
    setRedPenExplanation(null);
    setError(null);
    resetScreenForgeForm();

    if (feature.id === "signalscout") {
      setSsData(null);
      setSsError(null);
      fetchSignalScoutFeed();
    }

    if (feature.id === "regsentinel") {
      setRsData(null);
      setRsError(null);
      setRsRefreshMessage(null);
      fetchRegSentinelScan();
    }
  };

  const handleBackFromPrompt = () => {
    setSelectedFeature(null);
    setPrompt("");
    setGeneratedImage(null);
    setReferenceImageUrl(null);
    setUploadedImagesB64([]);
    setGeneratedAudioUrl(null);
    setGeneratedDraft(null);
    setRedPenExplanation(null);
    setError(null);
    resetScreenForgeForm();
  };

  const handleFileUpload = (file: File) => {
    if (!file.type.startsWith("image/")) {
      setError("Please upload a valid image file.");
      return;
    }

    if (uploadedImagesB64.length >= 2) {
      setError("You can upload a maximum of 2 images.");
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      setUploadedImagesB64((prev) => [
        ...prev,
        reader.result as string,
      ]);

      // Fresh upload overrides generated-image chain.
      setReferenceImageUrl(null);
      setGeneratedImage(null);
      setError(null);
    };

    reader.onerror = () => {
      setError("Unable to read the selected image.");
    };

    reader.readAsDataURL(file);
  };

  const handleRemoveUploadedImage = (index: number) => {
    setUploadedImagesB64((prev) =>
      prev.filter((_, imageIndex) => imageIndex !== index)
    );

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    setError(null);
  };

  const isContentGenerationFeature = (feature: Feature) => {
    return feature.id === "bravoquill" || feature.id === "bravomotion" || feature.id === "redpen_review";
  };

  const isBravoQuill =
    selectedFeature?.id === 'bravoquill';

  const isBravoMotion =
    selectedFeature?.id === 'bravomotion';

  const isCreativeTools =
    selectedFeature?.id === 'creative_tools';

  const isTextToAudio = selectedFeature?.id === 'text_to_audio';

  const isRedPen = selectedFeature?.id === 'redpen_review';

  const isSignalScout = selectedFeature?.id === 'signalscout';

  const isRegSentinel = selectedFeature?.id === 'regsentinel';

  const isScreenForge = selectedFeature?.formType === 'screenforge';

  // Field names aren't pinned down in the RegSentinel API doc, so try the
  // common ones the backend might use before giving up.
  const rsFindDate = (item: any): string | null =>
    item?.found_date ||
    item?.first_seen ||
    item?.date_found ||
    item?.tracked_at ||
    item?.date ||
    item?.published_date ||
    item?.posted_date ||
    item?.created_at ||
    null;

  const rsIsPdf = (item: any): boolean =>
    typeof item?.changes?.is_pdf === "boolean"
      ? item.changes.is_pdf
      : typeof item?.is_pdf === "boolean"
        ? item.is_pdf
        : /\.pdf(\?|$)/i.test(item?.url || "");

  const handleScreenshotUpload = (file: File) => {
    if (!file.type.startsWith("image/")) {
      setError("Please upload a valid image file.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      // API wants base64 without the "data:image/png;base64," prefix.
      const commaIndex = dataUrl.indexOf(",");
      setSfScreenshotB64(commaIndex >= 0 ? dataUrl.slice(commaIndex + 1) : dataUrl);
      setSfScreenshotMime(file.type || "image/png");
      setSfScreenshotPreview(dataUrl);
      setError(null);

      // Derive the screen name from the file itself (strip extension) —
      // no separate manual field.
      const dotIndex = file.name.lastIndexOf(".");
      setSfScreenName(dotIndex > 0 ? file.name.slice(0, dotIndex) : file.name);
    };
    reader.onerror = () => setError("Unable to read the selected screenshot.");
    reader.readAsDataURL(file);
  };

  // convert_screen and conversion_by_key/<key> are siblings under the same
  // .../screenforge/ base — derive the poll URL from whatever endpoint is
  // actually configured (local dev vs canary) instead of hardcoding a host.
  const getScreenForgePollUrl = (retryKey: string) =>
    selectedFeature!.apiEndpoint.replace(/\/convert_screen\/?$/, "/conversion_by_key/") + retryKey;

  const pollScreenForgeConversion = async (retryKey: string) => {
    setSfPolling(true);
    const pollUrl = getScreenForgePollUrl(retryKey);
    const maxAttempts = 20; // ~60s at 3s apart
    const delayMs = 3000;

    try {
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));

        const response = await fetch(pollUrl);
        if (!response.ok) {
          throw new Error(await parseApiErrorMessage(response));
        }
        const data = await response.json();
        setSfResult(data);

        const matched = data.matched?.[0];
        if (matched && matched.status !== "processing") {
          setSfConversionResult(matched);
          return;
        }
      }
      setError("ScreenForge is still processing — check back with the same screenshot later, or try again.");
    } catch (err: any) {
      setError(err.message || "Something went wrong while checking conversion status.");
    } finally {
      setSfPolling(false);
    }
  };

  const handleScreenForgeSubmit = async () => {
    setLoading(true);
    setError(null);
    setSfResult(null);
    setSfConversionResult(null);

    try {
      const response = await fetch(selectedFeature!.apiEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          screen_name: sfScreenName || generateFallbackScreenName(),
          old_html: sfOldHtml,
          old_css: sfOldCss || undefined,
          screenshot_base64: sfScreenshotB64,
          screenshot_mime: sfScreenshotMime,
          persona: "Office staff",
          outcome: 1,
          notes: sfNotes || undefined,
          safe_retry_key: sfRetryKeyRef.current,
          mode: "safe",
          employee_id: employeeID ? parseInt(employeeID) : undefined,
        }),
      });

      if (!response.ok) {
        throw new Error(await parseApiErrorMessage(response));
      }

      const data = await response.json();
      setSfResult(data);

      const matched = data.matched?.[0];
      if (matched && matched.status === "processing") {
        // Fire-and-forget: polling manages its own loading state (sfPolling)
        // separately from the initial submit's `loading`.
        pollScreenForgeConversion(matched.safe_retry_key || sfRetryKeyRef.current);
      } else if (matched) {
        setSfConversionResult(matched);
      }
    } catch (err: any) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const downloadTextAsFile = (content: string, filename: string, mime: string) => {
    const blob = new Blob([content], { type: `${mime};charset=utf-8` });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Shared by any conversion record we have full detail for (new_html/
  // new_css present) — used from the assets-list modal below.
  const downloadConversionAssets = (conversion: any) => {
    if (!conversion) return;
    const baseName = conversion.screen_name || "screenforge-asset";

    if (conversion.new_html) {
      downloadTextAsFile(conversion.new_html, `${baseName}.html`, "text/html");
    }
    if (conversion.new_css) {
      downloadTextAsFile(conversion.new_css, `${baseName}.css`, "text/css");
    }
    // Any image-ish field (name unconfirmed against the real response) —
    // download whatever's actually present under a reasonable filename.
    Object.entries(conversion).forEach(([key, value]) => {
      if (typeof value !== "string") return;
      const looksLikeImage = /image|screenshot|preview/i.test(key);
      const isUsableUrl = value.startsWith("http") || value.startsWith("data:image");
      if (looksLikeImage && isUsableUrl) {
        const ext = value.startsWith("data:image/") ? value.split(";")[0].split("/")[1] : "png";
        if (value.startsWith("data:")) {
          const link = document.createElement("a");
          link.href = value;
          link.download = `${baseName}-${key}.${ext}`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          handleDownload(value, `${baseName}-${key}.${ext}`);
        }
      }
    });
  };

  // convert_screen / conversions / conversion/<id> are siblings under the
  // same .../screenforge/ base.
  const getScreenForgeBaseUrl = () =>
    selectedFeature!.apiEndpoint.replace(/\/convert_screen\/?$/, "");

  const handleOpenAssetsList = async () => {
    setSfAssetsModalOpen(true);
    setSfSelectedConversion(null);
    setSfConversionsError(null);

    if (!employeeID) {
      setSfConversionsError("No employee id available.");
      return;
    }

    setSfConversionsLoading(true);
    try {
      const base = getScreenForgeBaseUrl();
      const response = await fetch(`${base}/conversions?employee_id=${employeeID}`);
      if (!response.ok) {
        throw new Error(await parseApiErrorMessage(response));
      }
      const data = await response.json();
      setSfConversionsList(data.matched || []);
    } catch (err: any) {
      setSfConversionsError(err.message || "Failed to load conversions.");
    } finally {
      setSfConversionsLoading(false);
    }
  };

  const handleSelectConversion = async (conversionListItem: any) => {
    setSfConversionDetailLoading(true);
    setSfSelectedConversion(null);
    setSfConversionsError(null);

    try {
      const base = getScreenForgeBaseUrl();
      const response = await fetch(`${base}/conversion/${conversionListItem.id}`);
      if (!response.ok) {
        throw new Error(await parseApiErrorMessage(response));
      }
      const data = await response.json();
      setSfSelectedConversion(data.matched?.[0] || data);
    } catch (err: any) {
      setSfConversionsError(err.message || "Failed to load conversion details.");
    } finally {
      setSfConversionDetailLoading(false);
    }
  };

  const handleCloseAssetsModal = () => {
    setSfAssetsModalOpen(false);
    setSfSelectedConversion(null);
    setSfConversionsError(null);
  };


  const handleSend = async () => {
    if (!prompt.trim()) {
      setError("Please enter a prompt before sending.");
      return;
    }

    if (!selectedFeature) return;

    setLoading(true);
    setError(null);

    setGeneratedImage(null);
    setGeneratedAudioUrl(null);
    setGeneratedDraft(null);
    setRedPenExplanation(null)

    try {
      const isRunFeature =
        isBravoQuill ||
        isBravoMotion ||
        isCreativeTools || isRedPen;

      let body: any;

      if (isRunFeature) {
        let intent = "";
        let context: any = {};

        if (isBravoQuill) {
          intent = "bravoquill_compose_standalone";

          context = {
            topic: prompt,
          };
        }

        if (isBravoMotion) {
          intent = "bravomotion_compose_script_standalone";

          context = {
            topic: prompt,
          };
        }

        if (isRedPen) {
          intent = "redpen_review";

          context = {
            text: prompt,
          };
        }

        if (isCreativeTools) {
          const totalImages =
            uploadedImagesB64.length + (referenceImageUrl ? 1 : 0);

          // 2 images:
          // uploaded + uploaded
          // uploaded + previously generated
          if (totalImages >= 2) {
            intent = "creativetools_merge_images";

            context = {
              prompt: prompt,

              // Fresh uploaded images
              reference_images: [...uploadedImagesB64],

              // Previously generated image
              reference_image_urls: referenceImageUrl
                ? [referenceImageUrl]
                : [],
            };
          }

          // Exactly 1 image
          else if (totalImages === 1) {
            intent = "creativetools_generate_image_from_reference";

            context = {
              prompt: prompt,

              // Uploaded image
              ...(uploadedImagesB64.length === 1
                ? {
                  reference_image: uploadedImagesB64[0],
                  reference_image_url: null,
                }
                : referenceImageUrl
                  ? {
                    // Previously generated image
                    reference_image_url: referenceImageUrl,
                  }
                  : {}),
            };
          }

          // No reference image
          else {
            intent = "creativetools_generate_image_from_prompt";

            context = {
              prompt: prompt,
            };
          }
        }

        body = {
          intent,
          context,
          task_id: null,
          goal_id: null,
          wizkind_task_id: null,
          wizkind_goal_id: null,
          template_task_id: null,
          template_id: null,
          employee_id: employeeID
        };
      } else {
        body = {
          prompt,
        };
      }

      const response = await fetch(
        selectedFeature.apiEndpoint,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Request failed with status ${response.status}`
        );
      }

      const data = await response.json();

      if (isCreativeTools) {
        const resultUrl =
          data.matched?.[0]?.supabase_url ?? null;

        if (!resultUrl) {
          throw new Error(
            "No generated image URL found in the response."
          );
        }

        setGeneratedImage(resultUrl);

        // Generated image becomes the reference for the next request.
        setReferenceImageUrl(resultUrl);
        // Uploaded images are single-use. The generated image now becomes
        // the chaining reference for subsequent requests.
        setUploadedImagesB64([]);

        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }

      else if (isBravoQuill) {
        const draftText =
          data.matched?.[0]?.draft_text ?? null;

        if (!draftText) {
          throw new Error(
            "No generated content found in the response."
          );
        }

        setGeneratedDraft(draftText);
      }

      else if (isBravoMotion) {
        const scriptText =
          data.matched?.[0]?.script_text ?? null;

        if (!scriptText) {
          throw new Error(
            "No generated video script found in the response."
          );
        }

        setGeneratedDraft(scriptText);
      }

      else if (isRedPen) {
        const verdict =
          data.matched?.[0]?.verdict ?? null;

        const explanation =
          data.matched?.[0]?.explanation ?? null;

        if (!verdict) {
          throw new Error(
            "No generated response found in the response."
          );
        }

        setGeneratedDraft(verdict);
        setRedPenExplanation(explanation);
      }

      else {
        const resultUrl =
          data.matched?.[0]?.supabase_url ?? null;

        if (!resultUrl) {
          throw new Error(
            "No result URL found in the response."
          );
        }

        if (selectedFeature.icon === "image") {
          setGeneratedImage(resultUrl);
        } else {
          setGeneratedAudioUrl(resultUrl);
        }
      }

      setPrompt("");

    } catch (err: any) {
      setError(
        err.message ||
        "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (url: string, filename: string) => {
    const link = document.createElement("a");

    link.href = url;
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadDraft = () => {
    if (!generatedDraft) return;

    const blob = new Blob([generatedDraft], {
      type: "text/plain;charset=utf-8",
    });

    const url = URL.createObjectURL(blob);

    handleDownload(url, `${selectedFeature?.id || "generated"}-draft.txt`);

    URL.revokeObjectURL(url);
  };

  const hasResult =
    generatedImage !== null ||
    generatedAudioUrl !== null ||
    generatedDraft !== null;


  /* ── Features Selection Screen ────────────────── */
  if (!selectedFeature) {
    return (
      <div
        id="cts-respon-p-dv"
        className="w-full relative overflow-hidden contain flex-row items-start justify-start flex-wrap content-start block py-40 px-20 mq991:p-8 mq575:p-4 box-border text-left text-base text-gray font-manrope"
      >
        {/* Header */}
        <div className="grid items-center gap-3 w-full ">
          <div className="flex-1 flex flex-col items-start justify-start gap-6 mb-6">
            <BreadCumb />

            <div className="self-stretch flex flex-row items-start justify-start gap-[5px] text-2xl w-full">
              <img
                className="w-6 h-6 overflow-hidden shrink-0 relative top-[7px] cursor-pointer dark-white-img mq575:top-[0px]"
                alt=""
                src={backArrow}
                onClick={() => navigate(-1)}
              />

              <div className="flex gap-2 items-start justify-start w-full">
                <div>
                  <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block mq450:text-base dark:text-white">
                    {agent.role}
                  </h3>

                  <p
                    className="text-xs leading-tight"
                    style={{ color: "#878787" }}
                  >
                    {agent.name}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <section
          id="agent-features"
          className="m-0 self-stretch rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 mq575:p-3 box-border gap-spacing-lg max-w-full mq825:pt-5 mq825:pb-5 mq1425:pt-[21px] mq1425:pb-[21px]"
        >
          {agent.features.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-4 py-20">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{ background: "#f0f8ff" }}
              >
                <LockIcon />
              </div>

              <p className="text-base font-semibold text-headings">
                Coming Soon
              </p>

              <p
                className="text-sm text-center"
                style={{
                  color: "#878787",
                  maxWidth: 300,
                }}
              >
                Features for {agent.role} are not available yet.
              </p>
            </div>
          ) : (
            <>
              <h5
                className="my-0  crelative font-bold text-xl-dv mt-0apitalize dark:text-white"
                style={{ color: "#2b2b2b" }}
              >
                Select a feature to get started
              </h5>

              <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-2 mq950:grid-cols-2 mq575:grid-cols-1 gap-[16px] min-w-full">
                {agent.features.map((feature) => (
                  <button
                    key={feature.id}
                    onClick={() => handleFeatureSelect(feature)}
                    className="flex flex-col items-center justify-center agent-feature-item-dv gap-4 py-10 px-5 rounded-2xl shadow-md border-2 border-transparent transition-all hover:shadow-xl"
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.borderColor = feature.accentColor)
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.borderColor = "transparent")
                    }
                  >
                    <div
                      className="w-16 h-16 rounded-full flex items-center justify-center"
                      style={{
                        background: feature.accentColor,
                      }}
                    >
                      {feature.icon === "image" ? (
                        <ImageFeatureIcon />
                      ) : feature.icon === "screen" ? (
                        <ScreenFeatureIcon />
                      ) : (
                        <AudioFeatureIcon />
                      )}
                    </div>

                    <div className="grid gap-1">
                      <h5 className="my-0  crelative font-bold text-xl-dv mt-0apitalize dark:text-white">
                        {feature.label}
                      </h5>

                      <p
                        className="text-sm text-center my-0"
                        style={{ color: "#2b2b2b" }}
                      >
                        {feature.description}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </section>
      </div>
    );
  }

  /* ── Prompt + Result Screen ───────────────────── */
  return (
    <div
      id="cts-respon-p-dv"
      className="w-full relative overflow-hidden contain flex-row items-start justify-start flex-wrap content-start py-20 px-20 mq991:p-8 mq575:p-4 box-border text-left text-base text-gray font-manrope"
    >
      {/* Header */}
      <div className="grid items-center gap-3">
        <div className="flex-1 flex flex-col items-start justify-start gap-6 mb-6">
          <BreadCumb />

          <div className="self-stretch flex flex-row items-start justify-start gap-[5px] text-2xl w-full">
            <img
              className="w-6 h-6 overflow-hidden shrink-0 relative top-[7px] cursor-pointer dark-white-img"
              alt=""
              src={backArrow}
              onClick={() => handleBackFromPrompt()}
            />

            <div className="flex gap-2 items-start justify-start w-full">
              <div>
                <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block mq450:text-base dark:text-white">
                  {agent.role}
                </h3>

                <p
                  className="text-xs leading-tight"
                  style={{ color: "#878787" }}
                >
                  {agent.name}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isScreenForge ? (
        <section
          id="agent-prompt-screenforge"
          className="m-0 self-stretch relative rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 mq575:p-3 box-border gap-6 max-w-full mq825:pt-5 mq825:pb-5 mq1425:pt-[21px] mq1425:pb-[21px]"
        >
          <p className="my-0 text-sm" style={{ color: "#878787" }}>
            {selectedFeature.helpText}
          </p>

          <div className="w-full grid gap-1.5">
            <label className="text-sm font-semibold dark:text-white">Legacy Screen HTML</label>
            <textarea
              value={sfOldHtml}
              onChange={(e) => setSfOldHtml(e.target.value)}
              placeholder="Paste the legacy screen's HTML..."
              disabled={loading}
              rows={6}
              className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none font-mono resize-y"
              style={{ borderColor: "#e7eff1" }}
            />
          </div>

          <div className="w-full grid gap-1.5">
            <label className="text-sm font-semibold dark:text-white">Legacy Screen CSS</label>
            <textarea
              value={sfOldCss}
              onChange={(e) => setSfOldCss(e.target.value)}
              placeholder="Paste the legacy screen's CSS, if any..."
              disabled={loading}
              rows={4}
              className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none font-mono resize-y"
              style={{ borderColor: "#e7eff1" }}
            />
          </div>

          <div className="w-full grid gap-4">
            <div className="grid gap-1.5">
              <label className="text-sm font-semibold dark:text-white">Screenshot</label>
              <input
                ref={sfScreenshotInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleScreenshotUpload(file);
                  e.target.value = "";
                }}
              />
              <button
                type="button"
                onClick={() => sfScreenshotInputRef.current?.click()}
                disabled={loading}
                className="px-3 py-2.5 rounded-lg border text-sm font-medium flex items-center gap-2 w-fit"
                style={{ borderColor: "#e7eff1" }}
              >
                <img src={UploadButtonimg} className="w-5 h-5" alt="" />
                {sfScreenshotPreview ? "Replace Screenshot" : "Upload Screenshot"}
              </button>
              {sfScreenshotPreview && (
                <img
                  src={sfScreenshotPreview}
                  alt="Screenshot preview"
                  className="mt-2 rounded-lg border max-h-40 object-contain"
                  style={{ borderColor: "#e7eff1" }}
                />
              )}
              <p className="text-xs my-0" style={{ color: "#878787" }}>
                {sfScreenName
                  ? <>Screen name (from file): <strong>{sfScreenName}</strong></>
                  : "Upload a screenshot to set the screen name from its filename."}
              </p>
            </div>
          </div>

          <div className="w-full grid gap-1.5">
            <label className="text-sm font-semibold dark:text-white">Notes</label>
            <textarea
              value={sfNotes}
              onChange={(e) => setSfNotes(e.target.value)}
              placeholder="Why is this screen being converted?"
              disabled={loading}
              rows={3}
              className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none resize-y"
              style={{ borderColor: "#e7eff1" }}
            />
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleScreenForgeSubmit}
              disabled={loading || sfPolling}
              className="px-6 py-2.5 rounded-lg text-sm font-semibold text-white flex items-center gap-2"
              style={{
                background: loading || sfPolling ? "#dbdfe6" : selectedFeature.accentColor,
                cursor: loading || sfPolling ? "not-allowed" : "pointer",
              }}
            >
              {loading ? <SpinnerIcon /> : null}
              {loading ? "Converting…" : "Convert Screen"}
            </button>

            <button
              type="button"
              onClick={handleOpenAssetsList}
              className="px-6 py-2.5 rounded-lg text-sm font-semibold border flex items-center gap-2"
              style={{
                borderColor: selectedFeature.accentColor,
                color: selectedFeature.accentColor,
                cursor: "pointer",
              }}
            >
              <DownloadIcon />
              View Assets
            </button>

            {sfPolling && (
              <span className="text-sm flex items-center gap-2" style={{ color: "#878787" }}>
                <SpinnerIcon /> Waiting for ScreenForge to finish…
              </span>
            )}
          </div>

          {error && (
            <div
              className="w-full rounded-lg p-3 text-sm whitespace-pre-wrap"
              style={{ background: "#fff0ee", border: "1px solid #fd605d", color: "#e01d40" }}
            >
              {error}
            </div>
          )}

          {sfResult && (
            <div className="w-full grid gap-2">
              <h5 className="my-0 font-bold text-lg dark:text-white">Result</h5>
              <pre
                className="w-full overflow-x-auto rounded-lg p-4 text-xs whitespace-pre-wrap"
                style={{ background: "#f7f9fa", border: "1px solid #e7eff1" }}
              >
                {JSON.stringify(sfResult, null, 2)}
              </pre>
            </div>
          )}
        </section>
      ) : isSignalScout ? (
        <section
          id="agent-prompt-signalscout"
          className="m-0 self-stretch relative rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 mq575:p-3 box-border gap-6 max-w-full mq825:pt-5 mq825:pb-5 mq1425:pt-[21px] mq1425:pb-[21px]"
        >
          <div className="w-full flex flex-wrap items-center justify-between gap-3">
            <p className="my-0 text-sm" style={{ color: "#878787" }}>
              {selectedFeature.helpText}
            </p>

            <div className="flex flex-wrap items-center gap-2">
              <div className="flex flex-wrap items-center gap-1.5">
                {SIGNALSCOUT_DAY_OPTIONS.map((days) => (
                  <button
                    key={days}
                    type="button"
                    disabled={ssLoading}
                    onClick={() => {
                      setSsDays(days);
                      fetchSignalScoutFeed(days);
                    }}
                    className="text-xs font-semibold px-3 py-1.5 rounded-full border"
                    style={
                      days === ssDays
                        ? {
                            background: selectedFeature.accentColor,
                            borderColor: selectedFeature.accentColor,
                            color: "#fff",
                          }
                        : {
                            background: "transparent",
                            borderColor: "#e7eff1",
                            color: "#878787",
                          }
                    }
                  >
                    {days === 1 ? "24h" : `${days}d`}
                  </button>
                ))}
              </div>

              <button
                type="button"
                onClick={() => fetchSignalScoutFeed()}
                disabled={ssLoading}
                className="text-sm font-medium px-4 py-2 rounded-lg border"
                style={{
                  borderColor: selectedFeature.accentColor,
                  color: selectedFeature.accentColor,
                  opacity: ssLoading ? 0.6 : 1,
                }}
              >
                {ssLoading ? "Refreshing…" : "Refresh"}
              </button>
            </div>
          </div>

          {ssError && (
            <div
              className="w-full rounded-lg p-3 text-sm"
              style={{ background: "#fff0ee", border: "1px solid #fd605d", color: "#e01d40" }}
            >
              {ssError}
            </div>
          )}

          {ssLoading && !ssData ? (
            <div className="w-full flex justify-center py-20">
              <SpinnerIcon />
            </div>
          ) : ssData && ssData.dates_covered?.length ? (
            <div className="w-full grid gap-6">
              <p className="my-0 text-xs" style={{ color: "#878787" }}>
                {ssData.total_cards ?? 0} signals across last {ssData.days_requested ?? ssDays} days
              </p>

              {ssData.dates_covered.map((date: string) => {
                const platforms = ssData.by_date?.[date] || {};
                const platformNames = Object.keys(platforms);

                return (
                  <div key={date} className="w-full grid gap-3">
                    <h5 className="my-0 font-bold text-base dark:text-white">{date}</h5>

                    {platformNames.length === 0 ? (
                      <p className="text-sm my-0" style={{ color: "#878787" }}>
                        No signals for this date.
                      </p>
                    ) : (
                      platformNames.map((platform) => {
                        const cards = platforms[platform] || [];
                        if (!cards.length) return null;

                        return (
                          <div key={platform} className="w-full grid gap-2">
                            <span
                              className="text-xs font-semibold uppercase w-fit px-2.5 py-1 rounded-full"
                              style={{ background: "#f0f8ff", color: "#019fd5" }}
                            >
                              {platform} · {cards.length}
                            </span>

                            <div className="grid gap-2">
                              {cards.map((card: any, idx: number) => (
                                <div
                                  key={card.id || card.url || idx}
                                  className="w-full rounded-lg border p-3 grid gap-1"
                                  style={{ borderColor: "#e7eff1" }}
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="text-sm font-semibold dark:text-white">
                                      {card.title || card.tag || "Untitled signal"}
                                    </span>

                                    {card.status && (
                                      <span
                                        className="text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0"
                                        style={{ background: "#f0f8ff", color: "#019fd5" }}
                                      >
                                        {card.status}
                                      </span>
                                    )}
                                  </div>

                                  {card.description && (() => {
                                    const readMoreMatch = card.description.match(
                                      /^([\s\S]*?)\.{0,3}\s*Read more\s*$/i
                                    );
                                    const mainText = readMoreMatch
                                      ? readMoreMatch[1]
                                      : card.description;

                                    return (
                                      <p className="text-sm my-0" style={{ color: "#5b5b5b" }}>
                                        {mainText}
                                        {readMoreMatch &&
                                          (card.url ? (
                                            <a
                                              href={card.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="font-medium"
                                              style={{ color: selectedFeature.accentColor }}
                                            >
                                              {" "}
                                              Read more
                                            </a>
                                          ) : (
                                            " Read more"
                                          ))}
                                      </p>
                                    );
                                  })()}

                                  {card.url && (
                                    <a
                                      href={card.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs font-medium w-fit"
                                      style={{ color: selectedFeature.accentColor }}
                                    >
                                      View source ↗
                                    </a>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            !ssLoading && (
              <p className="text-sm text-center w-full py-20" style={{ color: "#878787" }}>
                No signals in the last 7 days.
              </p>
            )
          )}
        </section>
      ) : isRegSentinel ? (
        <section
          id="agent-prompt-regsentinel"
          className="m-0 self-stretch relative rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 mq575:p-3 box-border gap-6 max-w-full mq825:pt-5 mq825:pb-5 mq1425:pt-[21px] mq1425:pb-[21px]"
        >
          <p className="my-0 text-sm" style={{ color: "#878787" }}>
            Georgia Medicaid / DCH home-care regulation watch — read-only
          </p>

          <div className="w-full flex flex-wrap items-center justify-between gap-3">
            <span
              className="text-xs font-semibold px-3 py-1.5 rounded-full w-fit"
              style={{ background: "#f0f8ff", color: "#019fd5" }}
            >
              Last {rsData?.diagnostics?.window?.match(/\d+/)?.[0] || REGSENTINEL_DAYS} days
            </span>

            <button
              type="button"
              onClick={handleRegSentinelRefresh}
              disabled={rsRefreshing}
              className="text-sm font-medium px-4 py-2 rounded-lg border"
              style={{
                borderColor: selectedFeature.accentColor,
                color: selectedFeature.accentColor,
                opacity: rsRefreshing ? 0.6 : 1,
              }}
            >
              {rsRefreshing ? "Queuing…" : "Refresh sources"}
            </button>
          </div>

          {rsRefreshMessage && (
            <div
              className="w-full rounded-lg p-3 text-sm"
              style={{ background: "#f0f8ff", border: "1px solid #019fd5", color: "#016a91" }}
            >
              {rsRefreshMessage}
            </div>
          )}

          {rsError && (
            <div
              className="w-full rounded-lg p-3 text-sm"
              style={{ background: "#fff0ee", border: "1px solid #fd605d", color: "#e01d40" }}
            >
              {rsError}
            </div>
          )}

          {rsLoading && !rsData ? (
            <div className="w-full flex justify-center py-20">
              <SpinnerIcon />
            </div>
          ) : rsData?.matched ? (
            (() => {
              const changedItems = rsData.matched.filter((m: any) => m.kind === "changed");
              const freshlyTracked = rsData.matched.filter(
                (m: any) => m.kind === "freshly_tracked"
              );
              const newItems = rsData.matched.filter((m: any) => m.kind === "new_item");

              return (
                <div className="w-full grid gap-8">
                  <div className="w-full grid grid-cols-3 mq575:grid-cols-1 gap-3">
                    {[
                      { count: changedItems.length, label: "Changed documents" },
                      { count: freshlyTracked.length, label: "Freshly tracked" },
                      { count: newItems.length, label: "New notices / posts" },
                    ].map((stat) => (
                      <div
                        key={stat.label}
                        className="rounded-lg border p-4 grid gap-1 text-center"
                        style={{ borderColor: "#e7eff1" }}
                      >
                        <span className="text-2xl font-bold dark:text-white">{stat.count}</span>
                        <span className="text-xs" style={{ color: "#878787" }}>
                          {stat.label}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="w-full grid gap-3">
                    <div>
                      <h5 className="my-0 font-bold text-base dark:text-white">Changed</h5>
                      <p className="my-0 text-xs" style={{ color: "#878787" }}>
                        what actually changed, point by point
                      </p>
                    </div>

                    {changedItems.length === 0 ? (
                      <p className="text-sm my-0" style={{ color: "#878787" }}>
                        No changes in this window.
                      </p>
                    ) : (
                      changedItems.map((item: any, idx: number) => (
                        <div
                          key={item.url || idx}
                          className="w-full rounded-lg border p-3 grid gap-2"
                          style={{ borderColor: "#e7eff1" }}
                        >
                          <div className="flex items-center gap-2">
                            <span
                              className="text-xs font-semibold uppercase px-2.5 py-1 rounded-full"
                              style={{ background: "#f0f8ff", color: "#019fd5" }}
                            >
                              {rsIsPdf(item) ? "PDF" : "Website"}
                            </span>

                            {item.agency && (
                              <span className="text-xs" style={{ color: "#878787" }}>
                                {item.agency}
                              </span>
                            )}
                          </div>

                          <span className="text-sm font-semibold dark:text-white">
                            {item.title}
                          </span>

                          {item.url && (
                            <a
                              href={item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs break-all w-fit"
                              style={{ color: "#878787" }}
                            >
                              {item.url}
                            </a>
                          )}

                          {rsFindDate(item) && (
                            <span className="text-xs" style={{ color: "#878787" }}>
                              found {rsFindDate(item)}
                            </span>
                          )}

                          <div className="grid gap-2 mt-1">
                            {item.changes?.edited?.map((e: any, i: number) => (
                              <div key={`e${i}`} className="text-xs grid gap-0.5">
                                <span className="font-semibold dark:text-white">
                                  EDITED{e.old_page ? ` · page ${e.old_page}` : ""}
                                </span>
                                <span style={{ color: "#e01d40" }}>− {e.old}</span>
                                <span style={{ color: "#1f9254" }}>+ {e.new}</span>
                              </div>
                            ))}

                            {item.changes?.added?.map((a: any, i: number) => (
                              <div key={`a${i}`} className="text-xs grid gap-0.5">
                                <span className="font-semibold dark:text-white">
                                  ADDED{a.page ? ` · page ${a.page}` : ""}
                                </span>
                                <span style={{ color: "#1f9254" }}>+ {a.text}</span>
                              </div>
                            ))}

                            {item.changes?.removed?.map((r: any, i: number) => (
                              <div key={`r${i}`} className="text-xs grid gap-0.5">
                                <span className="font-semibold dark:text-white">
                                  REMOVED{r.page ? ` · page ${r.page}` : ""}
                                </span>
                                <span style={{ color: "#e01d40" }}>− {r.text}</span>
                              </div>
                            ))}

                            {!rsIsPdf(item) && (
                              <p className="text-xs italic my-0" style={{ color: "#878787" }}>
                                not a PDF — no page number to show
                              </p>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="w-full grid gap-3">
                    <div>
                      <h5 className="my-0 font-bold text-base dark:text-white">
                        Freshly tracked
                      </h5>
                      <p className="my-0 text-xs" style={{ color: "#878787" }}>
                        first capture — nothing to compare yet
                      </p>
                    </div>

                    {freshlyTracked.length === 0 ? (
                      <p className="text-sm my-0" style={{ color: "#878787" }}>
                        Nothing newly tracked in this window.
                      </p>
                    ) : (
                      freshlyTracked.map((item: any, idx: number) => (
                        <div
                          key={item.url || idx}
                          className="w-full rounded-lg border p-3 grid gap-1"
                          style={{ borderColor: "#e7eff1" }}
                        >
                          <div className="flex items-center gap-2">
                            <span
                              className="text-xs font-semibold uppercase px-2.5 py-1 rounded-full"
                              style={{ background: "#f0f8ff", color: "#019fd5" }}
                            >
                              {rsIsPdf(item) ? "PDF" : "Website"}
                            </span>

                            {item.agency && (
                              <span className="text-xs" style={{ color: "#878787" }}>
                                {item.agency}
                              </span>
                            )}
                          </div>

                          <span className="text-sm font-semibold dark:text-white">
                            {item.title}
                          </span>

                          {item.url && (
                            <a
                              href={item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs break-all w-fit"
                              style={{ color: "#878787" }}
                            >
                              {item.url}
                            </a>
                          )}

                          {rsFindDate(item) && (
                            <span className="text-xs" style={{ color: "#878787" }}>
                              found {rsFindDate(item)}
                            </span>
                          )}
                        </div>
                      ))
                    )}
                  </div>

                  <div className="w-full grid gap-3">
                    <div>
                      <h5 className="my-0 font-bold text-base dark:text-white">
                        New notices / posts
                      </h5>
                      <p className="my-0 text-xs" style={{ color: "#878787" }}>
                        DCH public notices & the GEOH blog
                      </p>
                    </div>

                    {newItems.length === 0 ? (
                      <p className="text-sm my-0" style={{ color: "#878787" }}>
                        No new notices in this window.
                      </p>
                    ) : (
                      newItems.map((item: any, idx: number) => (
                        <div
                          key={item.url || idx}
                          className="w-full rounded-lg border p-3 grid gap-1"
                          style={{ borderColor: "#e7eff1" }}
                        >
                          <span className="text-sm font-semibold dark:text-white">
                            {item.title}
                          </span>

                          {item.agency && (
                            <span className="text-xs" style={{ color: "#878787" }}>
                              {item.agency}
                            </span>
                          )}

                          {item.url && (
                            <a
                              href={item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs break-all w-fit"
                              style={{ color: "#878787" }}
                            >
                              {item.url}
                            </a>
                          )}

                          {rsFindDate(item) && (
                            <span className="text-xs" style={{ color: "#878787" }}>
                              dated {rsFindDate(item)}
                            </span>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })()
          ) : (
            !rsLoading && (
              <p className="text-sm text-center w-full py-20" style={{ color: "#878787" }}>
                No RegSentinel data yet.
              </p>
            )
          )}

          <p className="text-xs italic text-center w-full my-0" style={{ color: "#878787" }}>
            Read-only. RegSentinel never drafts or publishes content from what it finds — a
            human or SignalScout/BravoQuill decides what to do with a sighting.
          </p>
        </section>
      ) : (
      <section
        id="agent-prompt"
        className="m-0 self-stretch relative rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 mq575:p-3 box-border gap-spacing-lg max-w-full agent-search-box-dv mq825:pt-5 mq825:pb-5 mq1425:pt-[21px] mq1425:pb-[21px]"
      >
        <div className="flex flex-col w-full items-center  overflow-y-auto ">
          <div className="w-full flex flex-col items-center gap-6">

            {
              isBravoMotion && !hasResult && !loading && <div className="text-center promt-t-heading-dv w-full grid gap-3 mt-40 chatimageplaceholder mq991:mt-20 mq575:mt-5">
                <div className="homelayout-promtscreen w-full"> <img src={videoCreateicon} placeholder-icon-item alt="" /></div>
                <h5 className="my-0 flex-1 text-xl-dv relative text-black inline-block dark:text-white">How can I help you with <br /><span>Content Generation for Video ?</span></h5>
                <p className="my-0 text-sm dark:text-white">Describe your topic</p>
              </div>
            }
            {
              isCreativeTools && !hasResult && !loading && <div className="text-center promt-t-heading-dv w-full grid gap-3 mt-40 chatimageplaceholder mq991:mt-20 mq575:mt-5">
                <div className="homelayout-promtscreen w-full"> <img src={imageIocnimg} placeholder-icon-item alt="" /></div>
                <h5 className="my-0 flex-1 text-xl-dv relative text-black inline-block dark:text-white">How can I help you with <br /><span>Image Generation ?</span></h5>
                <p className="my-0 text-sm dark:text-white">Describe your topic</p>
              </div>
            }


            {
              isTextToAudio && !hasResult && !loading &&
              <div className="text-center promt-t-heading-dv w-full grid gap-3 mt-40 chatimageplaceholder mq991:mt-20 mq575:mt-5">
                <div className="homelayout-promtscreen w-full"> <img src={audioIocnimg} placeholder-icon-item alt="" /></div>
                <h5 className="my-0 flex-1 text-xl-dv relative text-black inline-block dark:text-white">How can I help you with <br /><span>Audio Generation ?</span></h5>
                <p className="my-0 text-sm dark:text-white">Describe your topic</p>
              </div>
            }

            {
              isBravoQuill && !hasResult && !loading && <div className="text-center promt-t-heading-dv w-full grid gap-3 mt-40 chatimageplaceholder mq991:mt-20 mq575:mt-5">
                <div className="homelayout-promtscreen w-full"> <img src={contentCreateicon} placeholder-icon-item alt="" /></div>
                <h5 className="my-0 flex-1 text-xl-dv relative text-black inline-block dark:text-white">How can I help you with <br /><span>Content Generation ?</span></h5>
                <p className="my-0 text-sm dark:text-white">Describe your topic</p>
              </div>
            }

            {
              isRedPen && !hasResult && !loading && <div className="text-center promt-t-heading-dv w-full grid gap-3 mt-40 chatimageplaceholder mq991:mt-20 mq575:mt-5">
                <div className="homelayout-promtscreen w-full"> <img src={contentCreateicon} placeholder-icon-item alt="" /></div>
                <h5 className="my-0 flex-1 text-xl-dv relative text-black inline-block dark:text-white">How can I help you with <br /><span>Content Review ?</span></h5>
                <p className="my-0 text-sm dark:text-white">Describe your topic</p>
              </div>
            }


            {/* Help text */}
            {!hasResult && !loading && (
              <div className="flex items-center justify-center">
                <p className="text-xl font-semibold text-headings text-center">
                  {selectedFeature.helpText}
                </p>
              </div>
            )}

            {/* Loading */}
            {loading && (
              <div className="flex flex-col items-center gap-3 py-20">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center animate-pulse"
                  style={{ background: "#f0f8ff" }}
                >
                  <SpinnerIcon />
                </div>

                <p className="text-sm" style={{ color: "#878787" }}>
                  {isContentGenerationFeature(selectedFeature)
                    ? `Generating ${selectedFeature.label} content…`
                    : selectedFeature.icon === "image"
                      ? "Generating your image…"
                      : "Generating your audio…"}
                </p>
              </div>
            )}

            {/* Generated Image */}
            {/* {generatedImage && (
              <div className="w-full flex flex-col items-center gap-4">
                <div className="relative">
                  <img
                    src={generatedImage}
                    alt="Generated"
                    className="imagecreate-agent-dv object-contain rounded-xl shadow-lg"
                  />

                  <button
                    onClick={() =>
                      handleDownload(generatedImage, "generated-image.png")
                    }
                    className="cursor-pointer [border:none] py-2.5 h-[44px] download-btn-postion-item mq575:h-[35px] px-[15px] mq575:py-[12.5px] mq575:px-[25px] bg-surface-primary-surface rounded-radiuscomponent-button flex flex-row items-center justify-center gap-[12px]"
                    style={{
                      background: selectedFeature.accentColor,
                    }}
                  >
                    <DownloadIcon />
                  </button>
                </div>
              </div>
            )} */}

            {generatedImage && isCreativeTools && (
              <div className="w-full flex flex-col items-center gap-4">

                <div className="relative">
                  <img
                    src={generatedImage}
                    alt="Generated"
                    className="imagecreate-agent-dv object-contain rounded-xl shadow-lg"
                  />

                  <button
                    onClick={() =>
                      handleDownload(
                        generatedImage,
                        "generated-image.png"
                      )
                    }
                    className="cursor-pointer [border:none] py-2.5 h-[44px] download-btn-postion-item mq575:h-[35px] px-[15px] mq575:py-[12.5px] mq575:px-[25px] bg-surface-primary-surface rounded-radiuscomponent-button flex flex-row items-center justify-center gap-[12px]"
                    style={{
                      background: selectedFeature.accentColor,
                    }}
                  >
                    <DownloadIcon />
                  </button>
                </div>

                {/* Image Actions */}
                <div className="flex items-center gap-3">

                  {/* Continue editing current image */}
                  <button
                    type="button"
                    onClick={() => {
                      setReferenceImageUrl(generatedImage);
                      setPrompt("");
                    }}
                    className="px-5 py-2.5 rounded-lg text-sm font-medium"
                    style={{
                      background: selectedFeature.accentColor,
                      color: "#fff",
                    }}
                  >
                    Edit This Image
                  </button>

                  {/* Start completely new image */}
                  <button
                    type="button"
                    onClick={() => {
                      setGeneratedImage(null);
                      setReferenceImageUrl(null);
                      setPrompt("");
                      setError(null);
                    }}
                    className="px-5 py-2.5 rounded-lg text-sm font-medium border"
                  >
                    New Image
                  </button>

                </div>

              </div>
            )}

            {/* Generated Audio */}
            {generatedAudioUrl && (
              <div className="w-full flex items-center justify-center gap-4">
                <div className="flex justify-center items-center gap-4 w-full relative pt-[100px] mq991:pt-[20px] mq575:pt-[20px]">
                  <audio
                    ref={audioRef}
                    controls
                    src={generatedAudioUrl}
                    className="w-full"
                    style={{ maxWidth: 560 }}
                  >
                    Your browser does not support the audio element.
                  </audio>

                  <button
                    onClick={() =>
                      handleDownload(generatedAudioUrl, "generated-audio.mp3")
                    }
                    className="cursor-pointer [border:none] py-2.5 h-[44px] audio-download-btn-postion-item mq575:h-[35px] px-[15px] mq575:py-[12.5px] mq575:px-[25px] bg-surface-primary-surface flex flex-row items-center justify-center gap-[12px]"
                    style={{
                      background: selectedFeature.accentColor,
                    }}
                  >
                    <DownloadIcon />
                  </button>
                </div>
              </div>
            )}

            {/* Generated BravoQuill / BravoMotion Content */}
            {generatedDraft && isContentGenerationFeature(selectedFeature) && (
              <div className="w-full flex flex-col gap-4">
                <div className=" bg-white rounded-2xl shadow-md border border-gray-200 p-6">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-5 gap-4">
                    <div className="grid gap-4">
                      <h5 className="relative font-bold text-xl-dv mt-0 mb-[16px] capitalize dark:text-white">
                        Generated Content
                      </h5>

                      <p className="text-xs mt-1" style={{ color: "#878787" }}>
                        {selectedFeature.label}
                      </p>
                    </div>

                    <button
                      onClick={handleDownloadDraft}
                      className="cursor-pointer border-none py-2.5 px-4 rounded-lg download-conent-dv flex items-center justify-center gap-2 flex-shrink-0"
                      style={{
                        background: selectedFeature.accentColor,
                      }}
                    >
                      <DownloadIcon />

                      <span className="text-sm font-medium">Download</span>
                    </button>
                  </div>

                  <div className="redpen-agenct-section">
                    {/* Draft Content */}
                    <div
                      className="whitespace-pre-wrap text-sm conent-section-items leading-7 overflow-y-auto"
                      style={{
                        color: "#2b2b2b",

                      }}
                    >
                      {generatedDraft}
                    </div>

                    {/* RedPen Explanation */}
                    {isRedPen && redPenExplanation && (
                      <div className="border-t border-gray-200">
                        <h5 className="relative font-bold text-xl-dv mt-0 mb-[16px] capitalize dark:text-white">
                          Explanation
                        </h5>

                        <div
                          className="whitespace-pre-wrap text-sm leading-7"
                          style={{
                            color: "#555",
                          }}
                        >
                          {redPenExplanation}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div
                className="min-w-max alertmessage-video-g rounded-lg p-3 text-sm"
                style={{
                  background: "#fff0ee",
                  border: "1px solid #fd605d",
                  color: "#e01d40",
                }}
              >
                {error}
              </div>
            )}
          </div>

          {/* Fixed bottom prompt input */}
          <div
            className="absolute bottom-0 left-0 right-0 z-50 bg-white agent-feature-promt-item"
            style={{
              borderTop: "1px solid #e7eff1",
            }}
          >
            <div className="flex mq825:w-auto gap-3 items-start rounded-2xl px-4 py-3">
              {isCreativeTools && (
                <div className="flex items-center gap-2 flex-shrink-0 relative">

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      const files = e.target.files;

                      if (!files || files.length === 0) {
                        return;
                      }

                      const remainingSlots = 2 - uploadedImagesB64.length;

                      if (remainingSlots <= 0) {
                        setError("You can upload a maximum of 2 images.");
                        e.target.value = "";
                        return;
                      }

                      const selectedFiles = Array.from(files).slice(0, remainingSlots);

                      selectedFiles.forEach((file) => {
                        handleFileUpload(file);
                      });

                      if (files.length > remainingSlots) {
                        setError("You can upload a maximum of 2 images.");
                      }

                      e.target.value = "";
                    }}
                  />

                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={loading || uploadedImagesB64.length >= 2}
                    className="px-3 py-2 rounded-lg border text-sm flex items-center font-medium plus-task-btn res-agent-plus-task-btn taskdash-plush-img"
                    style={{
                      color:
                        loading || uploadedImagesB64.length >= 2
                          ? "#9ca3af"
                          : "#fff",
                      cursor:
                        loading || uploadedImagesB64.length >= 2
                          ? "not-allowed"
                          : "pointer",
                    }}
                  >
                    <img src={UploadButtonimg} className="w-5 h-5" alt="" /> <span className="text-sm dark:text-white mq575:hidden"> Upload Image</span>
                  </button>

{
  uploadedImagesB64?.length > 0 &&  <div className="absolute uploadimage-div-item">
                    {uploadedImagesB64.map((_, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleRemoveUploadedImage(index)}
                        disabled={loading}
                        className="text-xs font-medium cta-red"
                        style={{ color: "#e01d40" }}
                      >
                        Remove {index + 1}
                      </button>
                    ))}

                    {uploadedImagesB64.length > 0 && (
                      <span className="text-xs text-gray-500">
                        {uploadedImagesB64.length}/2 images
                      </span>
                    )}
                  </div>
}
                 
                </div>
              )}

              <textarea
                ref={textareaRef}
                value={prompt}
                onChange={(e) => {
                  setPrompt(e.target.value);

                  if (error) {
                    setError(null);
                  }

                  // Auto resize
                  e.target.style.height = "auto";

                  const maxHeight = 180;
                  const newHeight = Math.min(e.target.scrollHeight, maxHeight);

                  e.target.style.height = `${newHeight}px`;

                  // Scroll only after 250px
                  e.target.style.overflowY =
                    e.target.scrollHeight > maxHeight ? "auto" : "hidden";
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey && !loading) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder={
                  isCreativeTools && uploadedImagesB64.length > 0
                    ? "Describe what you want to generate using the uploaded image(s)..."
                    : isCreativeTools && referenceImageUrl
                      ? "Describe what you want to change in this image, or ask for a similar new image..."
                      : selectedFeature.placeholder
                }
                disabled={loading}
                rows={1}
                className="w-full resize-none border-none outline-none text-sm bg-transparent"
                style={{
                  color: "#2b2b2b",
                  height: "auto",
                  minHeight: "64px",
                  maxHeight: "100px",
                  overflowY: "hidden",
                }}
              />

              <button
                onClick={handleSend}
                disabled={loading || !prompt.trim()}
                className="self-end flex items-center justify-center agent-command-btn w-9 h-9 rounded-full flex-shrink-0 transition-colors"
                style={{
                  background:
                    loading || !prompt.trim()
                      ? "#dbdfe6"
                      : selectedFeature.accentColor,
                  cursor: loading || !prompt.trim() ? "not-allowed" : "pointer",
                }}
                aria-label="Send"
              >
                {loading ? <SpinnerIcon /> : <SendIcon />}
              </button>
            </div>
          </div>
        </div>
      </section>
      )}

      {sfAssetsModalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.5)" }}
          onClick={handleCloseAssetsModal}
        >
          <div
            className="w-full max-w-3xl max-h-[85vh] overflow-y-auto rounded-2xl bg-white dark:bg-slate-800 p-6 grid gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h5 className="my-0 font-bold text-lg dark:text-white">
                {sfSelectedConversion ? sfSelectedConversion.screen_name || "Conversion" : "ScreenForge Conversions"}
              </h5>
              <button
                type="button"
                onClick={sfSelectedConversion ? () => setSfSelectedConversion(null) : handleCloseAssetsModal}
                className="text-sm font-medium"
                style={{ color: "#878787" }}
              >
                {sfSelectedConversion ? "← Back to list" : "Close"}
              </button>
            </div>

            {sfConversionsError && (
              <div
                className="rounded-lg p-3 text-sm"
                style={{ background: "#fff0ee", border: "1px solid #fd605d", color: "#e01d40" }}
              >
                {sfConversionsError}
              </div>
            )}

            {/* Detail view */}
            {sfSelectedConversion ? (
              sfConversionDetailLoading ? (
                <div className="flex justify-center py-10">
                  <SpinnerIcon />
                </div>
              ) : (
                <div className="grid gap-3">
                  <p className="text-xs my-0" style={{ color: "#878787" }}>
                    Status: {sfSelectedConversion.status} · Created:{" "}
                    {sfSelectedConversion.created_at
                      ? new Date(sfSelectedConversion.created_at).toLocaleString()
                      : "—"}
                  </p>

                  {sfSelectedConversion.new_html ? (
                    <iframe
                      title="Conversion preview"
                      srcDoc={sfSelectedConversion.new_html}
                      sandbox=""
                      className="w-full rounded-lg border"
                      style={{ borderColor: "#e7eff1", height: "50vh" }}
                    />
                  ) : (
                    <p className="text-sm" style={{ color: "#878787" }}>
                      No HTML available for this conversion yet.
                    </p>
                  )}

                  <button
                    type="button"
                    onClick={() => downloadConversionAssets(sfSelectedConversion)}
                    className="px-6 py-2.5 rounded-lg text-sm font-semibold text-white flex items-center gap-2 w-fit"
                    style={{ background: selectedFeature?.accentColor }}
                  >
                    <DownloadIcon />
                    Download Assets
                  </button>
                </div>
              )
            ) : sfConversionsLoading ? (
              <div className="flex justify-center py-10">
                <SpinnerIcon />
              </div>
            ) : sfConversionsList.length === 0 ? (
              <p className="text-sm text-center py-10" style={{ color: "#878787" }}>
                No conversions found.
              </p>
            ) : (
              <div className="grid gap-2">
                {sfConversionsList.map((conv) => (
                  <button
                    key={conv.id}
                    type="button"
                    onClick={() => handleSelectConversion(conv)}
                    className="w-full text-left rounded-lg border p-3 flex items-center justify-between gap-3 hover:shadow-md transition-shadow"
                    style={{ borderColor: "#e7eff1" }}
                  >
                    <div className="grid gap-0.5">
                      <span className="text-sm font-semibold dark:text-white">{conv.screen_name}</span>
                      <span className="text-xs" style={{ color: "#878787" }}>
                        {conv.created_at ? new Date(conv.created_at).toLocaleString() : "—"}
                      </span>
                    </div>
                    <span
                      className="text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0"
                      style={{ background: "#f0f8ff", color: "#019fd5" }}
                    >
                      {conv.status}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentDetail;